/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState, useCallback } from "react";
import {
  fetchTopics as loadTopics,
  fetchTwoLevels as loadTwoLevels,
} from "../utils/topics";
import { LabelValue, findProductByLabel } from "../utils/tickets";
import { productData } from "settings/constants/constants";

export interface UseTopicsLoaderProps {
  api: any;
  globalProduct: string;
  isKontrolEdge: boolean;
}

export interface UseTopicsLoaderReturn {
  topics: LabelValue[];
  selectedTopicLabel: string;
  twoLevelOptions: LabelValue[];
  selectedTwoLevelLabel: string;
  loadingTopics: boolean;
  loadingTwoLevels: boolean;
  setSelectedTopicLabel: (label: string) => void;
  setSelectedTwoLevelLabel: (label: string) => void;
  handleSelectTopic: (topicLabel: string) => Promise<void>;
  resetTopics: () => void;
}

/**
 * Custom hook to manage topics and two-level options loading for Change Request modal
 */
export const useTopicsLoader = ({
  api,
  globalProduct,
  isKontrolEdge,
}: UseTopicsLoaderProps): UseTopicsLoaderReturn => {
  const [topics, setTopics] = useState<LabelValue[]>([]);
  const [selectedTopicLabel, setSelectedTopicLabel] = useState("");
  const [twoLevelOptions, setTwoLevelOptions] = useState<LabelValue[]>([]);
  const [selectedTwoLevelLabel, setSelectedTwoLevelLabel] = useState("");
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [loadingTwoLevels, setLoadingTwoLevels] = useState(false);

  // Load topics when component mounts (only for non-KE)
  useEffect(() => {
    if (isKontrolEdge) return;
    const found = findProductByLabel(productData, globalProduct);
    if (!found) return;
    
    let alive = true;
    setLoadingTopics(true);
    
    loadTopics(api, "Change Request", found.value)
      .then((d) => {
        if (alive) setTopics(d);
      })
      .catch((err) => console.error("loadTopics error:", err))
      .finally(() => {
        if (alive) setLoadingTopics(false);
      });
    
    return () => {
      alive = false;
    };
  }, [api, globalProduct, isKontrolEdge]);

  // Handle topic selection and load two-level options
  const handleSelectTopic = useCallback(
    async (topicLabel: string) => {
      setSelectedTopicLabel(topicLabel);
      setTwoLevelOptions([]);
      setSelectedTwoLevelLabel("");
      
      const productFound = findProductByLabel(productData, globalProduct);
      if (!productFound) return;

      setLoadingTwoLevels(true);
      try {
        const opts = await loadTwoLevels(
          api,
          "Change Request",
          productFound.value,
          topicLabel
        );
        setTwoLevelOptions(opts);
      } catch (e) {
        console.error("loadTwoLevels:", e);
      } finally {
        setLoadingTwoLevels(false);
      }
    },
    [api, globalProduct]
  );

  // Reset topics state
  const resetTopics = useCallback(() => {
    setTopics([]);
    setSelectedTopicLabel("");
    setTwoLevelOptions([]);
    setSelectedTwoLevelLabel("");
  }, []);

  return {
    topics,
    selectedTopicLabel,
    twoLevelOptions,
    selectedTwoLevelLabel,
    loadingTopics,
    loadingTwoLevels,
    setSelectedTopicLabel,
    setSelectedTwoLevelLabel,
    handleSelectTopic,
    resetTopics,
  };
};

